import { FC, ReactNode, useState } from "react";
import { IconButton, ListItem, ListItemButton, ListItemIcon, Tooltip, Typography } from "@mui/material";
import ConfirmDialog from "../../../../../viewComponents/ConfirmDialog";

interface Props {
	title: string;
	icon: ReactNode;
	menuIsOpen: boolean;
	onConfirm: () => void;

	// DISCUSS: should this property be typed and its type defined somewhere else?
	confirmOptions?: {
		title: string;
		details?: string;
		confirmText?: string;
	};
}

/**
 * AppMenuSelectItem renders a special single item of the application side menu which
 * allows users to perform an action that may require confirmation.
 *
 * If no confirmation configuration is provided, the action is performed without user consent.
 *
 * @param title          the item title
 * @param icon           the icon rendered when menu is closed
 * @param confirmOptions if defined, the button will ask user to confirm performing the action;
 *                       if defined, this object requires title property and allows the definition
 *                       of an optional details message and "confirm" button text
 * @param onConfirm      the action to be performed
 * @param menuIsOpen     this flag is used to correctly render the menu item
 * @returns              an item of the application side menu
 */
const ActionItem: FC<Props> = ({ title, icon, menuIsOpen, confirmOptions, onConfirm }) => {
	const [askConfirm, setAskConfirm] = useState<boolean>(false);

	const onClick = () => {
		if (confirmOptions) {
			setAskConfirm(true);
		} else {
			onConfirm();
		}
	};

	const confirmAction = () => {
		setAskConfirm(false);
		onConfirm();
	};

	const cancelAction = () => {
		setAskConfirm(false);
	};

	return (
		<>
			<ListItem disablePadding sx={{ display: "block" }}>
				{menuIsOpen && (
					<ListItemButton onClick={onClick} sx={{ minHeight: 48, justifyContent: "initial" }}>
						<ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>{icon}</ListItemIcon>
						<Typography>{title}</Typography>
					</ListItemButton>
				)}

				{!menuIsOpen && (
					<Tooltip arrow title={title} placement={"right"}>
						<IconButton onClick={onClick} sx={{ mx: 1.5 }}>
							{icon}
						</IconButton>
					</Tooltip>
				)}
			</ListItem>

			{confirmOptions && (
				<ConfirmDialog
					open={askConfirm}
					onCancel={cancelAction}
					onConfirm={confirmAction}
					title={confirmOptions.title}
					details={confirmOptions.details}
					confirmText={confirmOptions.confirmText}
				/>
			)}
		</>
	);
};

export default ActionItem;
